import { useCallback, useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import I18n from 'I18n';
import enviro from 'enviro';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
import { JOURNEY_ANALYTICS_READ_SCOPE } from 'reporting-data/constants/scopes';
import { SERVICE_JOURNEY_ANALYTICS_READ_SCOPE } from '../../constants/scopes';
import FilterEditor from './JourneyPropertyFilterEditor/FilterEditor';
import { getJourneyObjectTypeId } from './common/journeyHelpers';
import { useFetchProperties } from './JourneyPropertyFilterEditor/useFetchProperties';
import { REPORT_LEVEL_EVENT_FILTER_GROUP_ID } from './common/journeyHelpers';
import { buildPropertyFilters, constructNewReportFiltering, getTransformedAssociatedEventFilterSetFromQuery } from './JourneyPropertyFilterEditor/utils';
import FilterOverview from './JourneyPropertyFilterEditor/FilterOverview';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INFLUENCED_CAMPAIGN_EVENT = {
  value: 'e_object_influenced_marketing_campaign_v4',
  objectTypeId: {
    QA: '4-722622',
    PROD: '4-1563645'
  }
};
const ASSET_ID_PROPERTY = 'hs_asset_object_id';
const getInfluencedCampaignObjectTypeId = () => {
  return enviro.isQa() ? INFLUENCED_CAMPAIGN_EVENT.objectTypeId.QA : INFLUENCED_CAMPAIGN_EVENT.objectTypeId.PROD;
};
const JourneyAssociatedEventEditor = ({
  report,
  onChange
}) => {
  var _associatedEventFilte;
  const journeyReport = report.toJS();
  const associatedEventFilterSet = getTransformedAssociatedEventFilterSetFromQuery(journeyReport);
  const objectTypeId = getInfluencedCampaignObjectTypeId();
  const propertiesFetch = useFetchProperties(objectTypeId);
  const [currentFilter, setCurrentFilter] = useState(associatedEventFilterSet === null || associatedEventFilterSet === void 0 || (_associatedEventFilte = associatedEventFilterSet.filters) === null || _associatedEventFilte === void 0 ? void 0 : _associatedEventFilte[0]);
  const [activeEditingFilter, setActiveEditingFilter] = useState();
  const availableProperties = useMemo(() => {
    if (isSucceeded(propertiesFetch)) {
      const objectProperties = propertiesFetch.data;
      return Object.keys(objectProperties).filter(currentProperty => currentProperty !== ASSET_ID_PROPERTY).reduce((acc, currentProperty) => {
        acc[currentProperty] = objectProperties[currentProperty];
        return acc;
      }, {});
    }
    return {};
  }, [propertiesFetch]);
  const onApply = useCallback(newFilter => {
    const newFilterGroup = newFilter ? {
      id: REPORT_LEVEL_EVENT_FILTER_GROUP_ID,
      condition: 'AND',
      filters: buildPropertyFilters({
        filters: [newFilter]
      }, availableProperties, INFLUENCED_CAMPAIGN_EVENT.value)
    } : undefined;
    const newTableJoin = newFilter ? [{
      target: {
        name: INFLUENCED_CAMPAIGN_EVENT.value,
        objectTypeId,
        type: 'HUBSPOT_EVENT'
      }
    }] : undefined;
    setCurrentFilter(newFilter);
    const updatedReport = report.setIn(['journeyQuery', 'objectQuery', 'filtering'], fromJS(constructNewReportFiltering(report.toJS(), {
      associatedEvent: newFilterGroup,
      deleteAssociatedEvent: !newFilterGroup
    }))).setIn(['journeyQuery', 'objectQuery', 'table', 'join'], newTableJoin);
    setActiveEditingFilter(undefined);
    onChange(updatedReport);
  }, [availableProperties, objectTypeId, report, onChange]);
  const renderFilterContent = () => {
    if (isLoading(propertiesFetch)) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {});
    }
    if (isSucceeded(propertiesFetch)) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(TextInput, {
          value: I18n.text('reporting-enablement.reportEditors.journeyAssociatedEventsFilter.campaignInfluences'),
          readOnly: true,
          marginBlockEnd: "200"
        }), activeEditingFilter !== undefined ? /*#__PURE__*/_jsx(FilterEditor, {
          objectTypeId: objectTypeId,
          properties: availableProperties,
          currentFilter: currentFilter,
          apply: filter => {
            onApply(filter);
          },
          cancel: () => setActiveEditingFilter(undefined),
          isMultiFilterAllowed: false
        }) : /*#__PURE__*/_jsx(FilterOverview, {
          objectTypeId: objectTypeId,
          properties: availableProperties,
          filterSet: associatedEventFilterSet || {
            filters: []
          },
          setActiveEditingFilter: () => setActiveEditingFilter(0),
          deleteFilter: () => onApply(undefined),
          isMultiFilterAllowed: false
        })]
      });
    }
  };
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-enablement.reportEditors.journeyAssociatedEventsFilter.label"
    }),
    children: renderFilterContent()
  });
};
const isContactJourney = report => {
  const journeyReport = report.toJS();
  const objectId = getJourneyObjectTypeId(journeyReport);
  return objectId === '0-1';
};
JourneyAssociatedEventEditor.isUsableForReport = (report, gates, scopes) => isJourneyReport(report) && isContactJourney(report) && (scopes.includes(SERVICE_JOURNEY_ANALYTICS_READ_SCOPE) || scopes.includes(JOURNEY_ANALYTICS_READ_SCOPE));
export default JourneyAssociatedEventEditor;